import { Clock, Effect, PubSub, Queue, Scope, Stream, SubscriptionRef } from 'effect'
import type { RealtimeLifecycle, RealtimeLifecycleEvent, RealtimeVisibility } from './types'

export type TestRealtimeLifecycle = RealtimeLifecycle & {
  readonly emitVisibilityChanged: (visibility: RealtimeVisibility) => Effect.Effect<void>
  readonly emitPageHide: (persisted?: boolean) => Effect.Effect<void>
  readonly emitPageShow: (persisted?: boolean) => Effect.Effect<void>
}

/**
 * Browser lifecycle feed with listener registration tied to Scope.
 * One scoped instance == one set of DOM listeners.
 */
export function makeBrowserLifecycle(): Effect.Effect<RealtimeLifecycle, never, Scope.Scope> {
  return Effect.gen(function* () {
    const clock = yield* Clock.clockWith(Effect.succeed)
    let currentVisibility = readCurrentVisibility()
    const events = yield* PubSub.unbounded<RealtimeLifecycleEvent>()

    const publish = (makeEvent: (time: number) => RealtimeLifecycleEvent) => {
      const event = makeEvent(clock.currentTimeMillisUnsafe())
      currentVisibility = event.visibility
      PubSub.publishUnsafe(events, event)
    }

    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      const onVisibilityChange = () =>
        publish((time) => ({
          _tag: 'VisibilityChanged',
          visibility: readCurrentVisibility(),
          time
        }))

      const onPageHide = (event: PageTransitionEvent) =>
        // We preserve `persisted` so app-level policy can distinguish BFCache restores if needed.
        publish((time) => ({
          _tag: 'PageHide',
          visibility: readCurrentVisibility(),
          time,
          persisted: Boolean(event.persisted)
        }))

      const onPageShow = (event: PageTransitionEvent) =>
        publish((time) => ({
          _tag: 'PageShow',
          visibility: readCurrentVisibility(),
          time,
          persisted: Boolean(event.persisted)
        }))

      yield* Effect.acquireRelease(
        Effect.sync(() => {
          document.addEventListener('visibilitychange', onVisibilityChange)
          window.addEventListener('pagehide', onPageHide)
          window.addEventListener('pageshow', onPageShow)
        }),
        () =>
          Effect.sync(() => {
            document.removeEventListener('visibilitychange', onVisibilityChange)
            window.removeEventListener('pagehide', onPageHide)
            window.removeEventListener('pageshow', onPageShow)
          })
      )
    }

    return {
      currentVisibility: Effect.sync(() => currentVisibility),
      // PubSub stream gives every subscriber the same lifecycle feed (fan-out semantics).
      events: Stream.unwrap(PubSub.subscribe(events).pipe(Effect.map(Stream.fromSubscription)))
    } satisfies RealtimeLifecycle
  })
}

/**
 * Deterministic lifecycle feed for tests.
 */
export function makeTestLifecycle(
  initialVisibility: RealtimeVisibility = 'visible'
): Effect.Effect<TestRealtimeLifecycle> {
  return Effect.gen(function* () {
    const visibilityRef = yield* SubscriptionRef.make(initialVisibility)
    // Queue-backed stream keeps tests deterministic even if emits happen before runtime subscribes.
    const events = yield* Queue.unbounded<RealtimeLifecycleEvent>()

    const publish = (event: RealtimeLifecycleEvent) =>
      SubscriptionRef.set(visibilityRef, event.visibility).pipe(
        Effect.andThen(Queue.offer(events, event)),
        Effect.asVoid
      )
    const publishNow = (makeEvent: (time: number) => RealtimeLifecycleEvent) =>
      Clock.currentTimeMillis.pipe(Effect.flatMap((time) => publish(makeEvent(time))))

    const publishPageEvent = (_tag: 'PageHide' | 'PageShow', persisted: boolean) =>
      Effect.gen(function* () {
        // PageShow semantically implies visible regardless of previous state.
        let visibility: RealtimeVisibility
        switch (_tag) {
          case 'PageShow':
            visibility = 'visible'
            break
          case 'PageHide':
            visibility = yield* SubscriptionRef.get(visibilityRef)
            break
        }
        yield* publishNow((time) => ({ _tag, visibility, time, persisted }))
      })

    return {
      currentVisibility: SubscriptionRef.get(visibilityRef),
      events: Stream.fromQueue(events),
      emitVisibilityChanged: (visibility) =>
        publishNow((time) => ({
          _tag: 'VisibilityChanged',
          visibility,
          time
        })),
      emitPageHide: (persisted = false) => publishPageEvent('PageHide', persisted),
      emitPageShow: (persisted = false) => publishPageEvent('PageShow', persisted)
    } satisfies TestRealtimeLifecycle
  })
}

function readCurrentVisibility(): RealtimeVisibility {
  if (typeof document == 'undefined') {
    return 'visible'
  }

  return document.visibilityState == 'hidden' ? 'hidden' : 'visible'
}
